import React, { useState } from 'react';
import { Scenario, ScenarioRequest } from '../../types/sop';
import LoadingIndicator from '../common/LoadingIndicator';
import ErrorDisplay from '../common/ErrorDisplay';
import './ScenarioCreationWizard.css';

interface ScenarioCreationWizardProps {
  onScenarioCreate: (request: ScenarioRequest) => Promise<void>;
  onCancel: () => void;
  existingScenarios: Scenario[];
}

type WizardStep = 'basic' | 'assumptions' | 'ai-suggestions' | 'review';

interface ScenarioFormData {
  scenario_name: string;
  base_scenario_id?: string;
  assumptions: Record<string, any>;
  use_ai_suggestions: boolean;
}

const ScenarioCreationWizard: React.FC<ScenarioCreationWizardProps> = ({
  onScenarioCreate,
  onCancel,
  existingScenarios
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [formData, setFormData] = useState<ScenarioFormData>({
    scenario_name: '',
    base_scenario_id: undefined,
    assumptions: {},
    use_ai_suggestions: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<Record<string, any>>({});

  const handleBasicInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.scenario_name.trim()) {
      setError('Scenario name is required');
      return;
    }
    setError(null);
    setCurrentStep('assumptions');
  };

  const handleAssumptionsSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.use_ai_suggestions) {
      setCurrentStep('ai-suggestions');
      generateAISuggestions();
    } else {
      setCurrentStep('review');
    }
  };

  const generateAISuggestions = async () => {
    setLoading(true);
    try {
      // Simulate AI suggestions generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const suggestions = {
        demand_growth_rate: 0.15,
        supply_constraint_factor: 0.9,
        lead_time_variance: 1.2,
        seasonal_adjustment: 'Q4_peak',
        risk_tolerance: 'medium',
        optimization_priority: 'minimize_stockouts'
      };
      
      setAiSuggestions(suggestions);
      setFormData(prev => ({
        ...prev,
        assumptions: { ...prev.assumptions, ...suggestions }
      }));
    } catch (err) {
      setError('Failed to generate AI suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateScenario = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const request: ScenarioRequest = {
        scenario_name: formData.scenario_name,
        base_scenario_id: formData.base_scenario_id,
        assumptions: formData.assumptions
      };
      
      await onScenarioCreate(request);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create scenario');
    } finally {
      setLoading(false);
    }
  };

  const updateAssumption = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      assumptions: {
        ...prev.assumptions,
        [key]: value
      }
    }));
  };

  const renderBasicInfo = () => (
    <form onSubmit={handleBasicInfoSubmit} className="wizard-form">
      <h3>Basic Information</h3>
      
      <div className="form-group">
        <label htmlFor="scenario-name">Scenario Name *</label>
        <input
          id="scenario-name"
          type="text"
          value={formData.scenario_name}
          onChange={(e) => setFormData(prev => ({ ...prev, scenario_name: e.target.value }))}
          placeholder="e.g., Q2 High Demand Scenario"
          className="form-input"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="base-scenario">Base Scenario (Optional)</label>
        <select
          id="base-scenario"
          value={formData.base_scenario_id || ''}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            base_scenario_id: e.target.value || undefined 
          }))}
          className="form-select"
        >
          <option value="">Create from scratch</option>
          {existingScenarios.map(scenario => (
            <option key={scenario.scenario_id} value={scenario.scenario_id}>
              {scenario.scenario_name}
            </option>
          ))}
        </select>
        <small className="form-help">
          Choose an existing scenario to use as a starting point
        </small>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.use_ai_suggestions}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              use_ai_suggestions: e.target.checked 
            }))}
          />
          Use AI-generated suggestions for assumptions
        </label>
      </div>

      <div className="wizard-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Next: Assumptions
        </button>
      </div>
    </form>
  );

  const renderAssumptions = () => (
    <form onSubmit={handleAssumptionsSubmit} className="wizard-form">
      <h3>Planning Assumptions</h3>
      
      <div className="assumptions-grid">
        <div className="form-group">
          <label htmlFor="demand-growth">Demand Growth Rate (%)</label>
          <input
            id="demand-growth"
            type="number"
            step="0.01"
            value={formData.assumptions.demand_growth_rate || ''}
            onChange={(e) => updateAssumption('demand_growth_rate', parseFloat(e.target.value) || 0)}
            placeholder="0.10"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="supply-constraint">Supply Constraint Factor</label>
          <input
            id="supply-constraint"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={formData.assumptions.supply_constraint_factor || ''}
            onChange={(e) => updateAssumption('supply_constraint_factor', parseFloat(e.target.value) || 1)}
            placeholder="1.0"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="lead-time">Lead Time Variance</label>
          <input
            id="lead-time"
            type="number"
            step="0.1"
            min="0.1"
            value={formData.assumptions.lead_time_variance || ''}
            onChange={(e) => updateAssumption('lead_time_variance', parseFloat(e.target.value) || 1)}
            placeholder="1.0"
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="seasonal-adjustment">Seasonal Adjustment</label>
          <select
            id="seasonal-adjustment"
            value={formData.assumptions.seasonal_adjustment || ''}
            onChange={(e) => updateAssumption('seasonal_adjustment', e.target.value)}
            className="form-select"
          >
            <option value="">None</option>
            <option value="Q1_low">Q1 Low Season</option>
            <option value="Q2_moderate">Q2 Moderate</option>
            <option value="Q3_moderate">Q3 Moderate</option>
            <option value="Q4_peak">Q4 Peak Season</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="risk-tolerance">Risk Tolerance</label>
          <select
            id="risk-tolerance"
            value={formData.assumptions.risk_tolerance || ''}
            onChange={(e) => updateAssumption('risk_tolerance', e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="low">Low Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="high">High Risk</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="optimization-priority">Optimization Priority</label>
          <select
            id="optimization-priority"
            value={formData.assumptions.optimization_priority || ''}
            onChange={(e) => updateAssumption('optimization_priority', e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="minimize_cost">Minimize Cost</option>
            <option value="minimize_stockouts">Minimize Stockouts</option>
            <option value="maximize_service_level">Maximize Service Level</option>
            <option value="balance_all">Balance All Factors</option>
          </select>
        </div>
      </div>

      <div className="wizard-actions">
        <button 
          type="button" 
          onClick={() => setCurrentStep('basic')} 
          className="btn btn-secondary"
        >
          Back
        </button>
        <button type="submit" className="btn btn-primary">
          {formData.use_ai_suggestions ? 'Next: AI Suggestions' : 'Next: Review'}
        </button>
      </div>
    </form>
  );

  const renderAISuggestions = () => (
    <div className="wizard-form">
      <h3>AI-Generated Suggestions</h3>
      
      {loading ? (
        <LoadingIndicator message="Generating AI suggestions..." />
      ) : (
        <>
          <p className="ai-suggestions-intro">
            Based on historical data and market trends, here are AI-recommended adjustments to your assumptions:
          </p>
          
          <div className="ai-suggestions-list">
            {Object.entries(aiSuggestions).map(([key, value]) => (
              <div key={key} className="suggestion-item">
                <div className="suggestion-header">
                  <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                  <span className="suggestion-value">{String(value)}</span>
                </div>
                <div className="suggestion-actions">
                  <button
                    type="button"
                    onClick={() => updateAssumption(key, value)}
                    className="btn btn-sm btn-primary"
                  >
                    Apply
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const newSuggestions = { ...aiSuggestions };
                      delete newSuggestions[key];
                      setAiSuggestions(newSuggestions);
                    }}
                    className="btn btn-sm btn-secondary"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="wizard-actions">
            <button 
              type="button" 
              onClick={() => setCurrentStep('assumptions')} 
              className="btn btn-secondary"
            >
              Back
            </button>
            <button 
              type="button" 
              onClick={() => setCurrentStep('review')} 
              className="btn btn-primary"
            >
              Next: Review
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderReview = () => (
    <div className="wizard-form">
      <h3>Review & Create</h3>
      
      <div className="review-section">
        <h4>Scenario Details</h4>
        <div className="review-item">
          <strong>Name:</strong> {formData.scenario_name}
        </div>
        {formData.base_scenario_id && (
          <div className="review-item">
            <strong>Base Scenario:</strong> {
              existingScenarios.find(s => s.scenario_id === formData.base_scenario_id)?.scenario_name
            }
          </div>
        )}
      </div>

      <div className="review-section">
        <h4>Assumptions</h4>
        {Object.keys(formData.assumptions).length === 0 ? (
          <p className="no-assumptions">No assumptions defined</p>
        ) : (
          <div className="assumptions-review">
            {Object.entries(formData.assumptions).map(([key, value]) => (
              <div key={key} className="review-item">
                <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
                <span>{String(value)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="wizard-actions">
        <button 
          type="button" 
          onClick={() => setCurrentStep(formData.use_ai_suggestions ? 'ai-suggestions' : 'assumptions')} 
          className="btn btn-secondary"
        >
          Back
        </button>
        <button 
          type="button" 
          onClick={handleCreateScenario}
          disabled={loading}
          className="btn btn-primary"
        >
          {loading ? 'Creating...' : 'Create Scenario'}
        </button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'basic':
        return renderBasicInfo();
      case 'assumptions':
        return renderAssumptions();
      case 'ai-suggestions':
        return renderAISuggestions();
      case 'review':
        return renderReview();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="scenario-creation-wizard">
      <div className="wizard-header">
        <h2>Create New Scenario</h2>
        <div className="wizard-progress">
          <div className={`progress-step ${currentStep === 'basic' ? 'active' : ''}`}>
            Basic Info
          </div>
          <div className={`progress-step ${currentStep === 'assumptions' ? 'active' : ''}`}>
            Assumptions
          </div>
          {formData.use_ai_suggestions && (
            <div className={`progress-step ${currentStep === 'ai-suggestions' ? 'active' : ''}`}>
              AI Suggestions
            </div>
          )}
          <div className={`progress-step ${currentStep === 'review' ? 'active' : ''}`}>
            Review
          </div>
        </div>
      </div>

      {error && (
        <ErrorDisplay 
          error={error} 
          onRetry={() => setError(null)}
          operation="Scenario Creation"
        />
      )}

      <div className="wizard-content">
        {renderCurrentStep()}
      </div>
    </div>
  );
};

export default ScenarioCreationWizard;